// "Barkodu bas": which of a product's codes a label carries, how many copies, and where it prints
// (this computer's printer, or a packing desk's printer computer).
import { PRODUCT_LABEL_MAX_COPIES, parseLabelSize, type DeskProductLabelRequest, type DockBarcodesTaught } from '@wms/domain';

/** At most this many copies of one product's label in one print. */
export const MAX_LABEL_COPIES = PRODUCT_LABEL_MAX_COPIES;

/** A code a label can print: printable ASCII, 3–64 characters (a SKU with a Turkish letter or a space cannot). */
export const printableCode = (code: string): boolean => /^[!-~]{3,64}$/.test(code);

/** What the label's barcode says: the product's barcode, else its SKU (a scan finds the product by either); null when neither prints. */
export function labelCode(p: { readonly barcode: string | null; readonly sku: string }): string | null {
  if (p.barcode && printableCode(p.barcode)) return p.barcode;
  return printableCode(p.sku) ? p.sku : null;
}

/** The copies typed into a count box: digits only, at most MAX_LABEL_COPIES; 0 when nothing is typed. */
export function copyCount(typed: string): number {
  const digits = typed.replace(/\D/g, '');
  if (!digits) return 0;
  return Math.min(MAX_LABEL_COPIES, Number(digits.slice(0, 6)));
}

/** One of the product's codes a label can carry; unitsPerScan > 1: a carton's, one scan counts that many units. */
export interface LabelCodeChoice {
  readonly barcode: string;
  readonly unitsPerScan: number;
}

/**
 * The product's codes a label can carry, the unit's own first (the main one before the others),
 * then cartons from the smallest; unprintable ones left out. None at all: its SKU, if it prints
 * (sku null: no SKU instead).
 */
export function labelCodeChoices(
  barcodes: readonly { readonly barcode: string; readonly unitsPerScan: number; readonly isPrimary?: boolean }[],
  sku: string | null,
): LabelCodeChoice[] {
  const codes = barcodes
    .filter((b) => printableCode(b.barcode))
    .map((b, i) => ({ b, i }))
    .sort((x, y) => x.b.unitsPerScan - y.b.unitsPerScan || Number(y.b.isPrimary ?? false) - Number(x.b.isPrimary ?? false) || x.i - y.i)
    .map(({ b }) => ({ barcode: b.barcode, unitsPerScan: Math.max(1, b.unitsPerScan) }));
  const unique = codes.filter((c, i) => codes.findIndex((d) => d.barcode === c.barcode) === i);
  if (unique.length > 0 || sku === null) return unique;
  return printableCode(sku) ? [{ barcode: sku, unitsPerScan: 1 }] : [];
}

/** "8690000000036 · ürünün barkodu", "KOLI-12 · koli barkodu, 12 adet", "MD-KEP-01 · stok kodu" */
export function codeChoiceText(c: LabelCodeChoice, sku: string): string {
  if (c.unitsPerScan > 1) return `${c.barcode} · koli barkodu, ${c.unitsPerScan} adet`;
  return `${c.barcode} · ${c.barcode === sku ? 'stok kodu' : 'ürünün barkodu'}`;
}

/**
 * The codes saved with "Barkod ekle" at the dock as a label can print them: the ones just added first
 * (what "Barkodu bas" is pressed for), then the ones the product already had; each the unit before cartons.
 */
export function taughtCodes(t: DockBarcodesTaught): LabelCodeChoice[] {
  const codes = [...labelCodeChoices(t.added, null), ...labelCodeChoices(t.known, null)];
  return codes.filter((c, i) => codes.findIndex((d) => d.barcode === c.barcode) === i);
}

/** A desk as GET /api/wms/desk-printers lists it. */
export interface DeskPrinterLike {
  readonly code: string;
  readonly name: string;
  readonly browserPrinted: boolean;
  readonly designated: boolean;
  readonly online: boolean;
}

/** A desk whose printer computer can print product labels. */
export interface LabelDesk {
  readonly code: string;
  readonly name: string;
  readonly online: boolean;
}

/**
 * Desks a product label can be sent to: printed through a computer's browser, with a computer
 * switched on as the desk's printer ("Bu bilgisayar PK-01 masasının yazıcısı"); the ones heard
 * from lately first. A desk with nobody switched on would only keep the label waiting.
 */
export function labelDesks(desks: readonly DeskPrinterLike[]): LabelDesk[] {
  return desks
    .filter((d) => d.browserPrinted && d.designated)
    .map((d) => ({ code: d.code, name: d.name, online: d.online }))
    .sort((a, b) => Number(b.online) - Number(a.online) || a.code.localeCompare(b.code, 'tr'));
}

/** Where product labels print: 'here' this computer's printer, otherwise a desk's code. */
export type LabelTarget = 'here' | { readonly desk: string };

/** The roll remembered on this computer for product labels: its own printer's, and the desks' (kept apart). */
export const LABEL_SIZE_KEYS = { here: 'wms.productLabelSize', desk: 'wms.deskProductLabelSize' } as const;

/**
 * The roll a product label is drawn for: the size last used for that destination on this
 * computer, else the usual one: the product label roll here (50 × 30), the shipping label roll
 * a packing desk's printer is set up with (100 × 150, docs/yazici-kurulumu.md).
 */
export function labelSize(saved: string | null, target: LabelTarget): string {
  const size = saved ? parseLabelSize(saved) : null;
  if (size) return `${size.width}x${size.height}`;
  return target === 'here' ? '50x30' : '100x150';
}

/** One product's label as the dialog holds it. */
export interface LabelLine {
  readonly productId: string;
  readonly barcode: string;
  readonly copies: number;
}

/** The body of POST /api/wms/stations/:code/product-labels: the lines with copies, lot and SKT for one product only. */
export function deskLabelRequest(size: string, lines: readonly LabelLine[], lot: { readonly lotNo?: string; readonly expiryDate?: string } = {}): DeskProductLabelRequest {
  const lotNo = lot.lotNo?.trim() || null;
  const expiryDate = lot.expiryDate || null;
  return {
    size,
    labels: lines
      .filter((l) => l.copies > 0)
      .map((l) => ({ productId: l.productId, barcode: l.barcode, copies: Math.min(MAX_LABEL_COPIES, l.copies), ...(lotNo ? { lotNo } : {}), ...(expiryDate ? { expiryDate } : {}) })),
  };
}

/** "3 etiket PK-01 masasının yazıcısına gönderildi" and, when its computer is silent, when it will print. */
export function deskSentText(sent: { readonly station: string; readonly labels: number; readonly online: boolean }): string {
  const said = `${sent.labels} etiket ${sent.station} masasının yazıcısına gönderildi`;
  return sent.online ? said : `${said}; yazıcı bilgisayarı şu an çevrimdışı, açılınca basılır (bir saat içinde)`;
}
